import React, { useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  Chip,
  Stack,
  IconButton,
  Button,
  Grid,
  Fab,
  Paper
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Header from '../../Components/Header/Header';
import CloseIcon from "@mui/icons-material/Close";
import ErrorTwoToneIcon from '@mui/icons-material/ErrorTwoTone';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Define the shape of your user object
interface UserData {
  id: string;
  name: string;
  priority: string;
  subStatus: string;
  progress: number;
  accentColor?: string;
  bgColor?: string;
  showActions?: boolean;
  amountOwed?: string;
}

// Define the props for the component
interface UserCardProps {
  user: UserData;
}

// Apply the type to the component
const UserCard: React.FC<UserCardProps> = ({ user }) => {
  const isHighPriority = user.priority === 'HIGH';
  
  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: user.bgColor || '#FFFBEB', // Default yellowish
        border: '1px solid #F0F0F0',
        borderLeft: `5px solid ${user.accentColor || '#FFC107'}`,
        mb: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="subtitle1" fontWeight="700" sx={{ fontSize: '0.95rem' }}>
            {user.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {user.id}
          </Typography>
        </Box>
        <Chip
          label={user.priority}
          size="small"
          sx={{
            bgcolor: isHighPriority ? '#FFEBEE' : '#E0F2F1',
            color: isHighPriority ? '#D32F2F' : '#00796B',
            fontWeight: 'bold',
            fontSize: '0.65rem',
            height: 20,
          }}
        />
      </Stack>

      {/* Conditional Buttons (for Authorization column style) */}
      {user.showActions && (
        <Stack direction="row" spacing={1} mt={1}>
          <Button variant="outlined" size="small" sx={{ fontSize: '0.6rem', py: 0 }}>View</Button>
          <Button variant="outlined" size="small" sx={{ fontSize: '0.6rem', py: 0 }}>Message</Button>
        </Stack>
      )}

      {/* Conditional Amount (for Account Review column style) */}
      {user.amountOwed && (
        <Stack direction="row" justifyContent="space-between" mt={1.5}>
          <Typography variant="caption" color="text.secondary">Amount Owed</Typography>
          <Typography variant="caption" fontWeight="700">{user.amountOwed}</Typography>
        </Stack>
      )}

      <Box sx={{ mt: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight="500">Sub-Status</Typography>
          <Typography variant="caption" fontWeight="700">{user.subStatus}</Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={user.progress}
          sx={{ height: 6, borderRadius: 3, bgcolor: '#E0E0E0' }}
        />
      </Box>

      <Box sx={{ textAlign: 'center', mt: 0.5 }}>
        <IconButton size="small" sx={{ color: '#1976D2', p: 0 }}>
          <Typography variant="caption" fontWeight="600" sx={{ mr: 0.2 }}>More</Typography>
          <KeyboardArrowDownIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    </Card>
  );
};

// --- Main Dashboard Component ---
const NewDashboard = () => {
  const [openAiInsights, setOpenAiInsights] = useState(false);
  const columns = [
    {
      title: 'Registration',
      count: 1,
      avg: '50%',
      users: [
        { id: 'EA-2026-0423', name: 'Jennifer Martinez', priority: 'LOW', subStatus: 'Initial Review', progress: 40, accentColor: '#4A90E2', bgColor: '#F0F7FF' }
      ]
    },
    {
      title: 'Payment',
      count: 2,
      avg: '83%',
      users: [
        { id: 'EA-2026-0421', name: 'John Anderson', priority: 'LOW', subStatus: 'Pending', progress: 65, accentColor: '#FFC107', bgColor: '#FFFBEB' },
        { id: 'EA-2026-0422', name: 'Emily Chen', priority: 'LOW', subStatus: 'Payment Confirmed', progress: 100, accentColor: '#4CAF50', bgColor: '#FFFFFF' }
      ]
    },
    {
      title: 'Identity Verification',
      count: 2,
      avg: '18%',
      users: [
        { id: 'EA-2026-0417', name: 'James Wilson', priority: 'HIGH', subStatus: 'Failed', progress: 20, accentColor: '#F44336', bgColor: '#FFFFFF' },
        { id: 'EA-2026-0429', name: 'Kevin Thompson', priority: 'HIGH', subStatus: 'Abandoned 3 days ago', progress: 10, accentColor: '#F44336', bgColor: '#FFFFFF' }
      ]
    },
    {
      title: 'Authorization',
      count: 3,
      avg: '67%',
      bgColor: '#E0E4E8', // Slightly darker bg as seen in image
      users: [
        { id: 'EA-2026-0415', name: 'Michael Torres', priority: 'LOW', subStatus: 'Pending IRS Approval', progress: 60, accentColor: '#FFC107', bgColor: '#FFFBEB', showActions: true },
        { id: 'EA-2026-0430', name: 'Nicole Harris', priority: 'LOW', subStatus: 'Pending Client Signature', progress: 45, accentColor: '#FFC107', bgColor: '#FFFBEB' },
        { id: 'EA-2026-0431', name: 'Raymond Lee', priority: 'LOW', subStatus: 'IRS Approved', progress: 100, accentColor: '#4CAF50', bgColor: '#FFFFFF' }
      ]
    },
    {
      title: 'Account Review',
      count: 3,
      avg: '70%',
      bgColor: '#DBE4FF', // Blue tint bg
      users: [
        { id: 'EA-2026-0416', name: 'Robert Kim', priority: 'MEDIUM', subStatus: 'Pending IRS Approval', progress: 70, amountOwed: '$203,000', accentColor: '#FFC107', bgColor: '#FFFBEB' },
        { id: 'EA-2026-0427', name: 'Patricia Moore', priority: 'LOW', subStatus: 'Approved', progress: 100, amountOwed: '$145,000', accentColor: '#4CAF50', bgColor: '#FFFFFF' }
      ]
    }
  ];

  return (
    <>
    {/* <Header/> */}
      <Box sx={{ width: '100%', p: 2, bgcolor: '#F4F7F9', minHeight: '100vh' }}>
        <Stack 
          direction="row" 
          spacing={2} 
          sx={{ 
            overflowX: 'auto', 
            pb: 2,
            // Custom scrollbar for better appearance
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#BDC3C7', borderRadius: '4px' }
          }}
        >
          {columns.map((col, idx) => (
            <Box 
              key={idx} 
              sx={{ 
                minWidth: 280, 
                width: 280, 
                bgcolor: col.bgColor || '#F8F9FA', 
                borderRadius: 4, 
                p: 2,
                flexShrink: 0
              }}
            >
              {/* Column Header */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="800" color="#1A2027" noWrap>
                  {col.title}
                </Typography>
                <Box sx={{ 
                  width: 22, height: 22, borderRadius: '50%', border: '1px solid #E0E0E0', bgcolor: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700
                }}>
                  {col.count}
                </Box>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                <TrendingUpIcon sx={{ fontSize: 14, color: '#5F6368' }} />
                <Typography variant="caption" color="text.secondary" fontWeight="500">
                  {col.avg} avg completion
                </Typography>
              </Stack>

              <LinearProgress 
                variant="determinate" 
                value={parseInt(col.avg)} 
                sx={{ 
                  height: 4, borderRadius: 2, mb: 3, bgcolor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #4A90E2, #9B51E0)' }
                }} 
              />

              {/* List of Users in this column */}
              <Box>
                {col.users.map((user, uIdx) => (
                  <UserCard key={uIdx} user={user} />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    {/* Floating Button */}
      <Fab
        color="primary"
        onClick={() => setOpenAiInsights(!openAiInsights  )}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          px: '12px',
          py: '8px',
          width: 'auto',
          height: 'auto',
          borderRadius: '18px 18px',
          textTransform: 'none',
          background: "linear-gradient(90deg, #2F5AFB 0%, #9518FA 100%)",
          color: '#FFF',
        }}
      >
        AI Insights
      </Fab>
      {/* Open Container */}
      {openAiInsights && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: 5,
            right: 20,
            width: 320,
            height: 500,
            borderRadius: 3,
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              pt: 1,
              pb: 1,
              pl: 2,
              pr: 2,
              borderBottom: "1px solid #E5E7EB"
            }}
          >
            <Typography variant="subtitle1" fontWeight="800" color="#1A2027" noWrap>
              AI Insights
            </Typography>

            <IconButton onClick={() => setOpenAiInsights(false)}>
              <CloseIcon sx={{width:16, height:16}}/>
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, overflow: "auto", p:2, textAlign: 'left' }}>
            <Box sx={{ backgroundColor: '#fff4f0', p: '10px', border: '1px solid #FFD6A7', borderRadius: '6px' }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* Left Warning Icon */}
                <ErrorTwoToneIcon
                  color="warning"
                  sx={{ fontSize: 40, mt: 0.5 }}
                />

                {/* Right Content */}
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={500}
                    fontSize={16}
                    gutterBottom
                  >
                    Bottleneck Detected
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    12 cases stuck in Identity Verification for over 7 days
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#ffffff",
                  border: "1px solid #FFD6A7",
                  color: "#D43500",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: '12px',
                  px: '10px',
                  py: '6px',
                  fontWeight: '500',

                  "&:hover": {
                    backgroundColor: "#fff4f0",
                    border: "1px solid #FFD6A7",
                  },
                }}
              >
                <Box>View Cases</Box>

                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </Button>
            </Box>
            <Box sx={{ mt: 2, border: '1px solid #E5E7EB', p: 2, borderRadius: '6px' }}>
              {/* Heading */}
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                Recommended Actions
              </Typography>
            
              <Box>
                {/* List */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Box
                    key={1}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      border: '1px solid #BEDBFF',
                      backgroundColor: "#EFF6FF",
                      borderRadius: "12px",
                      p: 2
                    }}
                  >
                    {/* Dot */}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#155DFC",
                        mt: "7px",
                        flexShrink: 0,
                      }}
                    />

                    {/* Content */}
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ mb: 0.5 }}
                      >
                        Send reminder to 8 clients
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Missing documents in Identity Verification
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    key={2}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      border: '1px solid #BEDBFF',
                      backgroundColor: "#EFF6FF",
                      borderRadius: "12px",
                      p: 2
                    }}
                  >
                    {/* Dot */}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#155DFC",
                        mt: "7px",
                        flexShrink: 0,
                      }}
                    />

                    {/* Content */}
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ mb: 0.5 }}
                      >
                        Send reminder to 8 clients
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Missing documents in Identity Verification
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    key={3}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      border: '1px solid #BEDBFF',
                      backgroundColor: "#EFF6FF",
                      borderRadius: "12px",
                      p: 2
                    }}
                  >
                    {/* Dot */}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#155DFC",
                        mt: "7px",
                        flexShrink: 0,
                      }}
                    />

                    {/* Content */}
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ mb: 0.5 }}
                      >
                        Send reminder to 8 clients
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Missing documents in Identity Verification
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default NewDashboard;